import { UnitMeasurementSystem } from '@/constants/enums';
import { find, isEmpty, omit } from 'lodash';
import { action, makeObservable, observable } from 'mobx';
import { v4 as uuidv4 } from 'uuid';

import { DefaultBehaviour, ruleEditableFields } from './constants';
import type { BandSetpoints } from './constants';
import { convertFlowMinToImperial, convertPressureToImperial, convertPressureToMetric } from './utils';
import { Priority } from './SensorsConfiguration';

export class RulesBehaviour {
  @observable id: string;
  @observable defaultBehaviour: DefaultBehaviour;
  @observable ruleParentKey?: string;
  @observable rules?: Setpoint[];
  @observable oldValues: Partial<RulesBehaviour>;
  @observable sensorsPriority?: Priority[];

  constructor(rulesBehaviour, oldValues, tvalue?, unitMeasurementSystem?) {
    makeObservable(this);
    this.id = rulesBehaviour?.id ?? uuidv4();
    this.defaultBehaviour = rulesBehaviour?.defaultBehaviour;
    this.ruleParentKey = rulesBehaviour?.ruleParentKey;
    this.rules = rulesBehaviour?.rules?.map(
      rule =>
        new Setpoint({
          ...rule,
          ...(unitMeasurementSystem === UnitMeasurementSystem.Imperial && {
            load: convertPressureToImperial(rule?.load, tvalue),
            unload: convertPressureToImperial(rule?.unload, tvalue),
            autoStop: convertPressureToImperial(rule?.autoStop, tvalue),
            immediateStop: convertPressureToImperial(rule?.immediateStop, tvalue),
            vsdTarget: convertPressureToImperial(rule?.vsdTarget, tvalue),
          }),
        }),
    );
    this.oldValues = {
      id: oldValues?.id,
      defaultBehaviour: oldValues?.defaultBehaviour,
      ruleParentKey: oldValues?.ruleParentKey,
      rules: oldValues?.rules?.map(
        rule =>
          new Setpoint({
            ...rule,
            ...(unitMeasurementSystem === UnitMeasurementSystem.Imperial && {
              load: convertPressureToImperial(rule?.load, tvalue),
              unload: convertPressureToImperial(rule?.unload, tvalue),
              autoStop: convertPressureToImperial(rule?.autoStop, tvalue),
              immediateStop: convertPressureToImperial(rule?.immediateStop, tvalue),
              vsdTarget: convertPressureToImperial(rule?.vsdTarget, tvalue),
            }),
          }),
      ),
    };
  }

  @action setField(field, value, innerField, key) {
    if (field === 'rules' && innerField && key)
      this.rules = this.rules?.map(rule => (rule?.key === key ? new Setpoint({ ...rule, [innerField]: value }) : rule));
    else this[field] = value;
  }

  hasRuleChanges() {
    return (
      this.oldValues?.rules?.length &&
      this.rules?.some(rule =>
        ['name', ...ruleEditableFields].some(
          field => rule?.[field] !== find(this.oldValues?.rules, { key: rule.key })?.[field],
        ),
      )
    );
  }

  @action cancelRuleChanges() {
    this.rules = this.rules?.map(setpoint => {
      const oldDefaultSetpoint = find(this.oldValues?.rules, { key: setpoint.key });
      return new Setpoint({
        ...setpoint,
        load: oldDefaultSetpoint?.load,
        unload: oldDefaultSetpoint?.unload,
        autoStop: oldDefaultSetpoint?.autoStop,
        immediateStop: oldDefaultSetpoint?.immediateStop,
        vsdTarget: oldDefaultSetpoint?.vsdTarget,
        modulate: oldDefaultSetpoint?.modulate,
        bandConfiguration: oldDefaultSetpoint?.bandConfiguration,
        name: oldDefaultSetpoint?.name,
        pressureDrop: oldDefaultSetpoint?.pressureDrop,
      });
    });
  }

  hasChanges(oldValues?) {
    const rulesBehaviourOldValues = oldValues ?? this.oldValues;
    return (
      !isEmpty(rulesBehaviourOldValues) &&
      (this.defaultBehaviour !== rulesBehaviourOldValues?.defaultBehaviour ||
        this.ruleParentKey !== rulesBehaviourOldValues?.ruleParentKey ||
        this.hasRuleChanges())
    );
  }

  @action cancelChanges() {
    this.defaultBehaviour = this.oldValues?.defaultBehaviour;
    this.ruleParentKey = this.oldValues?.ruleParentKey;
    this.rules = this.oldValues?.rules;
  }

  getValuesToSave(tvalue, unitMeasurementSystem) {
    const rules =
      unitMeasurementSystem === UnitMeasurementSystem.Imperial
        ? this.rules?.map(setpoint => ({
            ...setpoint,
            load: convertPressureToMetric(setpoint?.load, tvalue),
            unload: convertPressureToMetric(setpoint?.unload, tvalue),
            autoStop: convertPressureToMetric(setpoint?.autoStop, tvalue),
            immediateStop: convertPressureToMetric(setpoint?.immediateStop, tvalue),
            vsdTarget: convertPressureToMetric(setpoint?.vsdTarget, tvalue),
          }))
        : this.rules;
    return omit({ ...this, rules }, 'oldValues');
  }
}

export class Setpoint {
  @observable key: string;
  @observable name: string;
  @observable compressorId: string;
  @observable compressorGroupId: string;
  @observable compressorNumber: string;
  @observable compressorName: string;
  @observable load: number;
  @observable unload: number;
  @observable autoStop: number;
  @observable immediateStop: number;
  @observable vsdTarget: number;
  @observable modulate: boolean;
  @observable bandConfiguration: number;
  @observable parentRangeKey?: string;
  @observable bandSetpoints?: BandSetpoints;
  @observable pressureDrop?: number;
  @observable order?: number;

  constructor(setpoint?) {
    makeObservable(this);
    this.key = setpoint?.key;
    this.name = setpoint?.name;
    this.compressorId = setpoint?.compressorId;
    this.compressorGroupId = setpoint?.compressorGroupId;
    this.compressorNumber = setpoint?.compressorNumber;
    this.compressorName = setpoint?.compressorName;
    this.load = setpoint?.load;
    this.unload = setpoint?.unload;
    this.autoStop = setpoint?.autoStop;
    this.immediateStop = setpoint?.immediateStop;
    this.vsdTarget = setpoint?.vsdTarget;
    this.modulate = setpoint?.modulate;
    this.bandConfiguration = setpoint?.bandConfiguration;
    this.parentRangeKey = setpoint?.parentRangeKey;
    this.bandSetpoints = setpoint?.bandSetpoints;
    this.pressureDrop = setpoint?.pressureDrop ?? 0;
    this.order = setpoint?.order;
  }

  @action setField(field, value) {
    this[field] = value;
  }
  static convertSetpointsToUnitMeasurementSystem(setpoints: Setpoint[], tvalue, unitMeasurementSystem, converterFunc) {
    const fields = ['load', 'unload', 'autoStop', 'immediateStop', 'vsdTarget', 'pressureDrop'];

    const processSetpoint = (setpoint, convert) => {
      const processed = { ...setpoint };
      fields.forEach(field => {
        processed[field] = roundOff(convert ? converterFunc(setpoint?.[field], tvalue) : setpoint?.[field]);
      });
      return new Setpoint(processed);
    };

    if (!Array.isArray(setpoints)) return setpoints;
    return setpoints.map(setpoint =>
      processSetpoint(setpoint, unitMeasurementSystem === UnitMeasurementSystem.Imperial),
    );
  }
}

export class StationParameter {
  @observable key: string;
  @observable stationTargetPressure?: number;
  @observable normalPressureOffsetFromTarget?: number;
  @observable reloadPressure?: number;
  @observable startPressure?: number;
  @observable emergencyStartPressure?: number;
  @observable minAllowedActiveCompressors?: number;
  @observable unloadingTestPressureFactor?: number;
  @observable minAllowedUnloadTestingAttemptRateMinutes?: number;
  @observable unloadCommandMinTargetSafetyPercentage?: number;
  @observable minAllowedStartTimeIntervalMinutes?: number;
  @observable minAllowedRestartTimeIntervalMinutes?: number;
  @observable unloadTestingFlowPeakBuffer?: number;
  @observable unloadTestingFlowBufferPercentage?: number;
  @observable minIntervalFromHighFlowTrigger?: number;
  @observable unloadTestingCommandFrequencySeconds?: number;
  @observable kpiTimeIntervalMinutes?: number;
  @observable loadBalancingPressureFactor?: number;
  @observable emergencyStartPriorityRotationDelayMinutes?: number;
  @observable compressorBovUpperBoundThresholdPercentage?: number;
  @observable bovAvgUpperBoundThresholdPercentage?: number;
  @observable maxLoadBuffer?: number;

  constructor(stationParameter: StationParameter) {
    makeObservable(this);
    Object.assign(this, stationParameter);
  }

  static convertStationParameterToUnitMeasurementSystem(
    stationParameters: StationParameter[],
    tvalue: number,
    unitMeasurementSystem: UnitMeasurementSystem,
  ): StationParameter[] {
    if (!stationParameters || !stationParameters.length || !stationParameters[0]) return [];
    return unitMeasurementSystem === UnitMeasurementSystem.Imperial
      ? stationParameters?.map(
          (stationParameter: StationParameter) =>
            new StationParameter({
              ...stationParameter,
              unloadCommandMinTargetSafetyPercentage: stationParameter.unloadCommandMinTargetSafetyPercentage * 100,
              unloadTestingFlowPeakBuffer: convertFlowMinToImperial(
                stationParameter.unloadTestingFlowPeakBuffer,
                tvalue,
              ),
              unloadTestingFlowBufferPercentage: stationParameter.unloadTestingFlowBufferPercentage * 100,
              compressorBovUpperBoundThresholdPercentage:
                stationParameter.compressorBovUpperBoundThresholdPercentage * 100,
              maxLoadBuffer: stationParameter.maxLoadBuffer * 100,
              bovAvgUpperBoundThresholdPercentage: stationParameter.bovAvgUpperBoundThresholdPercentage * 100,
              stationTargetPressure: convertPressureToImperial(stationParameter.stationTargetPressure, tvalue),
              unloadingTestPressureFactor: convertPressureToImperial(
                stationParameter.unloadingTestPressureFactor,
                tvalue,
              ),
              normalPressureOffsetFromTarget: convertPressureToImperial(
                stationParameter.normalPressureOffsetFromTarget,
                tvalue,
              ),
              loadBalancingPressureFactor: convertPressureToImperial(
                stationParameter.loadBalancingPressureFactor,
                tvalue,
              ),
              reloadPressure: convertPressureToImperial(stationParameter.reloadPressure, tvalue),
              startPressure: convertPressureToImperial(stationParameter.startPressure, tvalue),
              emergencyStartPressure: convertPressureToImperial(stationParameter.emergencyStartPressure, tvalue),
              key: uuidv4(),
            }),
        ) ?? []
      : stationParameters?.map(
          (stationParameter: StationParameter) =>
            new StationParameter({
              ...stationParameter,
              unloadCommandMinTargetSafetyPercentage:
                (stationParameter.unloadCommandMinTargetSafetyPercentage ?? 0) * 100,
              unloadTestingFlowBufferPercentage: (stationParameter.unloadTestingFlowBufferPercentage ?? 0) * 100,
              compressorBovUpperBoundThresholdPercentage:
                stationParameter.compressorBovUpperBoundThresholdPercentage * 100,
              maxLoadBuffer: stationParameter.maxLoadBuffer * 100,
              bovAvgUpperBoundThresholdPercentage: stationParameter.bovAvgUpperBoundThresholdPercentage * 100,
              key: uuidv4(),
            }),
        ) ?? [];
  }
}

export class CompressorParameter {
  @observable key: string;

  @observable deviceId: string;
  @observable compressorGroupId: string;
  @observable compressorNumber: string;
  @observable compressorName: string;
  @observable priority: number;
  @observable maxTarget: number;
  @observable minTarget: number;
  @observable stopDelayMinutes: number;
  @observable maxFlow: number;
  @observable maxLoadAmps: number;
  @observable fiveLoop: boolean;

  constructor(compressorParameter: CompressorParameter) {
    makeObservable(this);
    Object.assign(this, compressorParameter);
  }

  static convertCompressorParametersToUnitMeasurementSystem(
    compressorParameters,
    tvalue,
    unitMeasurementSystem,
    converterFunc,
  ) {
    return compressorParameters?.map(compressorParameter => {
      let fiveLoopValue = 'false';
      if (
        compressorParameter?.fiveLoop === 1 ||
        compressorParameter?.fiveLoop === true ||
        compressorParameter?.fiveLoop === 'true' ||
        compressorParameter?.fiveLoop === '1'
      ) {
        fiveLoopValue = 'true';
      }
      const convertedParameter = {
        ...compressorParameter,
        key: uuidv4(),
        maxTarget:
          unitMeasurementSystem === UnitMeasurementSystem.Imperial
            ? converterFunc(compressorParameter?.maxTarget, tvalue)
            : compressorParameter?.maxTarget,
        minTarget:
          unitMeasurementSystem === UnitMeasurementSystem.Imperial
            ? converterFunc(compressorParameter?.minTarget, tvalue)
            : compressorParameter?.minTarget,
        stopDelayMinutes: compressorParameter?.stopDelayMinutes ?? null,
        priority: compressorParameter?.priority ?? null,
        compressorName: compressorParameter?.name ?? null,
        compressorNumber: compressorParameter?.attributes?.compressorNumber ?? null,
        deviceId: compressorParameter?.deviceId ?? null,
        maxFlow: compressorParameter?.maxFlow ?? null,
        maxLoadAmps: compressorParameter?.maxLoadAmps ?? null,
        fiveLoop: fiveLoopValue,
      };
      return new CompressorParameter(convertedParameter);
    });
  }
}

export interface MetricSetpoint {
  metricType: string;
  value: number;
  deviceId: string;
}

export interface ValidRevertEngineData {
  isValidEngineRevert: boolean;
  message: string;
}

export type BandSetpointType =
  | 'minimumPressureBand1Bar'
  | 'maximumPressureBand1Bar'
  | 'minimumPressureBand2Bar'
  | 'maximumPressureBand2Bar';

export interface UpdateRangeAlerts {
  hasAcknowledgedFallback: boolean;
  hasAcknowledgedRule: boolean;
}

export const roundOff = v => (typeof v === 'number' ? parseFloat(Number(v).toFixed(4)) : v);
